/**
 * Keep withheld secrets out of the request snapshot a script sees.
 *
 * COL-02 says a withheld secret's VALUE never crosses into the sandbox worker,
 * and `buildSandboxPayload` enforced that for the variable maps only. The
 * request snapshot is a second way in: a test script receives the request as
 * it was SENT, so `Authorization: Bearer <token>` and a `?api_key=<token>` URL
 * arrive fully resolved. An imported collection's test script could copy that
 * header into an ordinary variable, and the next pre-request script ships it
 * out with `pm.sendRequest` (code review 24.09, 2A §2; card d21bee68).
 *
 * The snapshot keeps its shape — scripts that read the method, the host, a
 * content-type header keep working — and every occurrence of a withheld value
 * is put back as the `{{key}}` it came from, which is what the user wrote and
 * what the runner's pre-request path already hands scripts.
 */
import { isScriptWithheld } from './scriptVariables';

/**
 * `{ key: value }` of every withheld secret with a non-empty value, in every
 * scope. Every scope, not just the winning one: redacting a value that did not
 * end up in the request costs nothing, missing one that did is the leak.
 */
export function withheldSecretValues(scopes = {}, options = {}) {
  const out = {};
  const lists = [
    scopes.globalVariables,
    scopes.workspaceVariables,
    scopes.environmentVariables,
    scopes.collectionVariables,
    scopes.folderVariables,
  ];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const v of list) {
      if (!v?.key || !isScriptWithheld(v, options)) continue;
      const value = String(v.value ?? '');
      if (value) out[`${v.key}`] = value;
    }
  }
  return out;
}

/** Every spelling of a value that can end up in a URL or a header. */
function spellingsOf(value) {
  const spellings = new Set([value]);
  try {
    spellings.add(encodeURIComponent(value));
    spellings.add(encodeURI(value));
  } catch {
    // A lone surrogate cannot be URI-encoded; the raw spelling still counts.
  }
  return [...spellings].filter(Boolean);
}

/**
 * Longest first: a secret that contains another secret must be replaced whole,
 * or the shorter one would split it and leave the rest readable.
 */
function replacementsFor(withheld) {
  const pairs = [];
  for (const [key, value] of Object.entries(withheld || {})) {
    for (const spelling of spellingsOf(String(value))) pairs.push([spelling, `{{${key}}}`]);
  }
  return pairs.sort((a, b) => b[0].length - a[0].length);
}

function redactString(text, pairs) {
  if (typeof text !== 'string' || !text) return text;
  let out = text;
  for (const [needle, template] of pairs) {
    if (out.includes(needle)) out = out.split(needle).join(template);
  }
  return out;
}

/**
 * `Basic base64(user:pass)` hides the password from a substring search. Decode
 * it, and if a withheld value is inside, hand the script the scheme only.
 */
function redactBasic(headerValue, withheld) {
  const match = /^\s*basic\s+([A-Za-z0-9+/=]+)\s*$/i.exec(headerValue);
  if (!match) return headerValue;
  let binary = '';
  try {
    binary = typeof atob === 'function'
      ? atob(match[1])
      : Buffer.from(match[1], 'base64').toString('binary');
  } catch {
    return headerValue;
  }
  // The app encodes Basic credentials as UTF-8 (requestAuth.encodeBasicCredentials),
  // so a non-ASCII secret is only found in the UTF-8 reading; a header the user
  // typed by hand may be either, so both readings are searched.
  let utf8 = binary;
  if (typeof TextDecoder === 'function') {
    utf8 = new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
  }
  const hit = Object.values(withheld).some(
    (value) => value && (binary.includes(value) || utf8.includes(value))
  );
  return hit ? 'Basic {{withheld}}' : headerValue;
}

function redactHeaders(headers, withheld, pairs) {
  if (!headers || typeof headers !== 'object') return headers;
  if (Array.isArray(headers)) {
    return headers.map((h) => (h && typeof h === 'object'
      ? { ...h, value: redactString(redactBasic(String(h.value ?? ''), withheld), pairs) }
      : h));
  }
  const out = {};
  for (const [name, value] of Object.entries(headers)) {
    out[name] = typeof value === 'string'
      ? redactString(redactBasic(value, withheld), pairs)
      : value;
  }
  return out;
}

/**
 * The request snapshot with every withheld value put back as `{{key}}`.
 * Returns the input unchanged when nothing is withheld.
 */
export function redactWithheldInRequest(request, withheld) {
  if (!request || typeof request !== 'object') return request;
  const pairs = replacementsFor(withheld);
  if (!pairs.length) return request;
  return {
    ...request,
    url: redactString(request.url, pairs),
    headers: redactHeaders(request.headers, withheld, pairs),
    ...(typeof request.body === 'string' ? { body: redactString(request.body, pairs) } : {}),
  };
}

/**
 * The response snapshot carries the URL it was fetched from — the same URL,
 * the same leak. Headers and body are the SERVER's and are left alone.
 */
export function redactWithheldInResponse(response, withheld) {
  if (!response || typeof response !== 'object' || typeof response.url !== 'string') return response;
  const pairs = replacementsFor(withheld);
  if (!pairs.length) return response;
  return { ...response, url: redactString(response.url, pairs) };
}
